import { Check, Sparkles } from "lucide-react";
import { Button } from "../ui/button";
import Reveal from "../Reveal";

// Plans mirror the backend (app/api/v1/endpoints/plans.py): tiers Lite / Basic / Pro, each
// offered Monthly or Yearly (yearly = 10× monthly, i.e. two months free). India (₹) pricing.
// NOTE: backend marks these prices as placeholder — confirm before launch.
const plans = [
  {
    name: "Lite",
    desc: "The essentials for solo businesses.",
    priceMonthly: "₹99",
    priceYearlyNote: "or ₹990 / year",
    cta: "Get started",
    href: "#contact",
    features: [
      "Quick billing & receipts",
      "Item & stock management",
      "Daily sales summary",
      "Thermal printer support",
      "Single user",
    ],
  },
  {
    name: "Basic",
    desc: "Everything a growing team needs.",
    priceMonthly: "₹299",
    priceYearlyNote: "or ₹2,990 / year",
    cta: "Get started",
    href: "#contact",
    features: [
      "Everything in Lite",
      "Item variants, offers & discounts",
      "Item production (make-to-order)",
      "Customer & supplier ledger",
      "Advanced reporting",
      "Up to 5 users with staff roles",
    ],
  },
  {
    name: "Pro",
    desc: "Advanced tools for scaling businesses.",
    highlight: true,
    priceMonthly: "₹999",
    priceYearlyNote: "or ₹9,990 / year",
    cta: "Book a demo",
    href: "#contact",
    features: [
      "Everything in Basic",
      "Cloud sync across devices",
      "Up to 25 users with staff roles",
      "Multi-location support",
      "Batch, expiry & serial tracking",
      "Recurring invoices",
    ],
  },
];

export default function Pricing() {
  return (
    <section id="pricing" data-testid="pricing-section" className="relative py-24 lg:py-32 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto">
          <Reveal><p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-800">Pricing</p></Reveal>
          <Reveal delay={80}>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900 mt-3">
              Plans that scale with your business.
            </h2>
          </Reveal>
          <Reveal delay={140}>
            <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed">
              Start small and upgrade as you grow. Pay monthly, or yearly and get two months free.
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-4 items-stretch">
          {plans.map((p, i) => (
            <Reveal key={p.name} delay={i * 100}>
              <div
                data-testid={`pricing-card-${p.name.toLowerCase()}`}
                className={`relative h-full flex flex-col rounded-3xl p-8 lg:p-9 transition-all duration-300 ${
                  p.highlight
                    ? "bg-navy text-white shadow-2xl scale-100 lg:scale-[1.03] border border-blue-800/20"
                    : "bg-white border border-slate-200 hover:-translate-y-1 hover:shadow-lg"
                }`}
              >
                {p.highlight && (
                  <span className="absolute -top-3 left-8 inline-flex items-center gap-1.5 rounded-full bg-blue-800 text-white px-3 py-1 text-[11px] font-bold uppercase tracking-wider">
                    <Sparkles className="h-3 w-3" /> Most popular
                  </span>
                )}
                <h3 className={`font-display font-black text-2xl tracking-tight ${p.highlight ? "text-white" : "text-slate-900"}`}>
                  {p.name}
                </h3>
                <p className={`mt-2 text-sm ${p.highlight ? "text-slate-300" : "text-slate-600"}`}>{p.desc}</p>

                <div className={`mt-6 rounded-2xl border ${p.highlight ? "border-white/10 bg-white/5" : "border-slate-200 bg-slate-50"} p-5`}>
                  <p className={`font-display text-3xl font-black ${p.highlight ? "text-white" : "text-slate-900"}`}>
                    {p.priceMonthly}
                    <span className={`text-base font-semibold ${p.highlight ? "text-slate-400" : "text-slate-500"}`}> / month</span>
                  </p>
                  <p className={`text-xs ${p.highlight ? "text-slate-400" : "text-slate-500"} mt-1`}>
                    {p.priceYearlyNote}
                  </p>
                </div>

                <ul className="mt-6 space-y-3 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className={`flex items-start gap-3 text-sm ${p.highlight ? "text-slate-200" : "text-slate-700"}`}>
                      <span className="h-5 w-5 shrink-0 rounded-full grid place-items-center mt-0.5 bg-blue-800">
                        <Check className="h-3 w-3 text-white" />
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>

                <a href={p.href} className="mt-8">
                  <Button
                    data-testid={`pricing-cta-${p.name.toLowerCase()}`}
                    className={`w-full rounded-full py-6 font-semibold ${
                      p.highlight
                        ? "bg-blue-800 hover:bg-blue-700 text-white"
                        : "bg-navy hover:bg-navy/90 text-white"
                    }`}
                  >
                    {p.cta}
                  </Button>
                </a>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={300}>
          <div className="mt-12 rounded-3xl border border-slate-200 bg-white p-8 lg:p-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div>
              <h3 className="font-display font-bold text-2xl tracking-tight text-slate-900">Questions about a plan?</h3>
              <p className="mt-2 text-slate-600 max-w-xl">
                Not sure which tier fits, or need something tailored for a larger operation? Talk to us and
                we'll help you pick.
              </p>
            </div>
            <a href="#contact">
              <Button data-testid="custom-pricing-btn" className="rounded-full bg-navy hover:bg-navy/90 text-white px-7 py-6 font-semibold">
                Talk to us
              </Button>
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
